// frontend/src/pages/ExtratoMilhasPage/ExtratoMilhasPage.jsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Typography, Table, Button, Space, message, Spin, Tag, Card, Row, Col, Statistic, Select, Modal, Tooltip, List, DatePicker, Input, Switch } from 'antd';
import { 
    ArrowUpOutlined, ArrowDownOutlined, EditOutlined, DeleteOutlined, 
    CloseCircleOutlined, InfoCircleOutlined, CheckCircleFilled, 
    SwapOutlined, UserSwitchOutlined, ClockCircleOutlined, ShopOutlined,
    CalendarOutlined, ClearOutlined, SearchOutlined, RollbackOutlined
} from '@ant-design/icons';
import { getPortadores, getProgramasDoPortador, getProgramaPortadorDetail, getTransacoesDoPrograma, getTransacoesDoPortador, confirmarTransacaoMilhas, reverterTransacaoMilhasParaPendente, deleteTransacaoMilhas, confirmarBonusClube, cancelarEmissao, updateTransacaoMilhas, getOperacaoVenda } from '../../services/milhasService';
import { useDataRefresh } from '../../contexts/DataRefreshContext';
import { stripMarcadorFaturaId } from '../../utils/estimativaPontosCartao';
import dayjs from 'dayjs';
import TransacaoMilhasModal from '../../components/TransacaoMilhasModal/TransacaoMilhasModal';
import SaidaMilhasModal from '../../components/SaidaMilhasModal/SaidaMilhasModal';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import TransferenciaMilhasModal from '../../components/TransferenciaMilhasModal/TransferenciaMilhasModal';
import TransferenciaTerceirosModal from '../../components/TransferenciaTerceirosModal/TransferenciaTerceirosModal';
import LogoManager from '../../components/LogoManager/LogoManager';
import { getErrorMessage } from '../../utils/errorHandler';
import {
    STATUS_MILHAS_LABELS,
    isMilhasAReceber,
    operacaoLabel,
    subTipoLabel,
} from '../../utils/milhasLabels';

const { Title, Text } = Typography;
const { Option } = Select;

const ExtratoMilhasPage = () => {
    const navigate = useNavigate();
    const { portadorId: portadorIdParam, programaId: programaIdParam } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const filtroAReceber = searchParams.get('filtro') === 'a_receber';

    const deepLinkPortadorId = portadorIdParam ? Number(portadorIdParam) : null;
    const deepLinkProgramaId = programaIdParam ? Number(programaIdParam) : null;
    const pendingDeepLinkProgramaId = useRef(
        Number.isFinite(deepLinkProgramaId) && deepLinkProgramaId > 0 ? deepLinkProgramaId : null
    );

    const [portadores, setPortadores] = useState([]);
    const [programasDoPortador, setProgramasDoPortador] = useState([]);
    const [selectedPortadorId, setSelectedPortadorId] = useState(null);
    const [selectedProgramaId, setSelectedProgramaId] = useState(null);

    const [programaDetalhe, setProgramaDetalhe] = useState(null);
    const [transacoes, setTransacoes] = useState([]);
    
    const [loadingPortadores, setLoadingPortadores] = useState(true);
    const [loadingProgramas, setLoadingProgramas] = useState(false);
    const [loadingTransacoes, setLoadingTransacoes] = useState(false);
    
    const [isEntradaModalVisible, setIsEntradaModalVisible] = useState(false);
    const [isSaidaModalVisible, setIsSaidaModalVisible] = useState(false);
    const [isTransferenciaModalVisible, setIsTransferenciaModalVisible] = useState(false);
    const [isTransferenciaTerceirosModalVisible, setIsTransferenciaTerceirosModalVisible] = useState(false);

    const [editingTransaction, setEditingTransaction] = useState(null);
    const [editingSaida, setEditingSaida] = useState(null);
    const [editingVenda, setEditingVenda] = useState(null);

    const [filtroStatus, setFiltroStatus] = useState([]);
    const [filtroTipo, setFiltroTipo] = useState(null);
    const [filtroSubTipos, setFiltroSubTipos] = useState([]);
    const [filtroMes, setFiltroMes] = useState(null);
    const [filtroBusca, setFiltroBusca] = useState('');
    
    // Hooks do Ant Design
    const [modal, modalContextHolder] = Modal.useModal();
    const [messageApi, messageContextHolder] = message.useMessage();
    
    const [tableSorters, setTableSorters] = useState(null);

    const { refreshKey, triggerRefresh } = useDataRefresh();
    const isInitialLoad = useRef(true);

    // Hook de Responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleTableChange = (pagination, filters, sorter) => {
        setTableSorters(sorter);
    };

    // CÁLCULO DAS MILHAS A RECEBER (PENDENTES + PROJETADAS) — lista completa
    const milhasAReceber = useMemo(() => {
        if (!transacoes || transacoes.length === 0) return 0;
        
        return transacoes
            .filter(isMilhasAReceber)
            .reduce((acc, curr) => acc + parseFloat(curr.quantidade_milhas), 0);
    }, [transacoes]);

    const subTipoOptions = useMemo(() => {
        const seen = new Set();
        (transacoes || []).forEach((t) => {
            if (t.sub_tipo) seen.add(t.sub_tipo);
        });
        return Array.from(seen)
            .sort((a, b) => subTipoLabel(a).localeCompare(subTipoLabel(b), 'pt-BR'))
            .map((value) => ({ value, label: subTipoLabel(value) }));
    }, [transacoes]);

    const transacoesExibidas = useMemo(() => {
        let list = transacoes || [];
        if (filtroAReceber) {
            list = list.filter(isMilhasAReceber);
        }
        if (filtroStatus.length > 0) {
            list = list.filter((t) => filtroStatus.includes(t.status));
        }
        if (filtroTipo) {
            list = list.filter((t) => t.tipo_transacao === filtroTipo);
        }
        if (filtroSubTipos.length > 0) {
            list = list.filter((t) => filtroSubTipos.includes(t.sub_tipo));
        }
        if (filtroMes) {
            const y = filtroMes.year();
            const m = filtroMes.month();
            list = list.filter((t) => {
                const d = dayjs(t.data_transacao);
                return d.isValid() && d.year() === y && d.month() === m;
            });
        }
        const q = filtroBusca.trim().toLowerCase();
        if (q) {
            list = list.filter((t) => {
                const hay = [
                    stripMarcadorFaturaId(t.observacao),
                    t.programa_nome,
                    t.sub_tipo,
                    operacaoLabel(t),
                    t.detalhes_compra_bonificada?.loja,
                    t.detalhes_compra_bonificada?.produto,
                    t.projecao_info?.descricao,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return hay.includes(q);
            });
        }
        return list;
    }, [transacoes, filtroAReceber, filtroStatus, filtroTipo, filtroSubTipos, filtroMes, filtroBusca]);

    const temFiltrosAtivos = Boolean(
        filtroAReceber ||
        filtroStatus.length > 0 ||
        filtroTipo ||
        filtroSubTipos.length > 0 ||
        filtroMes ||
        filtroBusca.trim()
    );

    const setFiltroAReceber = (ligado) => {
        const next = new URLSearchParams(searchParams);
        if (ligado) next.set('filtro', 'a_receber');
        else next.delete('filtro');
        setSearchParams(next, { replace: true });
    };

    const limparFiltrosLista = () => {
        setFiltroStatus([]);
        setFiltroTipo(null);
        setFiltroSubTipos([]);
        setFiltroMes(null);
        setFiltroBusca('');
        setFiltroAReceber(false);
    };

    const isTodosProgramas = selectedProgramaId === 'todos';

    const portadorSelecionado = useMemo(
        () => portadores.find(p => p.id === selectedPortadorId) || null,
        [portadores, selectedPortadorId]
    );

    const totaisAgregados = useMemo(() => {
        if (!programasDoPortador.length) {
            return { saldo: 0, investimento: 0, qtdProgramas: 0 };
        }
        return {
            saldo: programasDoPortador.reduce((acc, p) => acc + parseFloat(p.saldo_atual || 0), 0),
            investimento: programasDoPortador.reduce((acc, p) => acc + parseFloat(p.valor_total_investido || 0), 0),
            qtdProgramas: programasDoPortador.length,
        };
    }, [programasDoPortador]);

    const fetchOnlyExtratoData = useCallback(async (programaId, portadorId) => {
        if (!programaId) return;
        if (programaId === 'todos' && !portadorId) return;
        setLoadingTransacoes(true);
        try {
            if (programaId === 'todos') {
                const transacoesRes = await getTransacoesDoPortador(portadorId);
                setProgramaDetalhe(null);
                setTransacoes(transacoesRes.data);
            } else {
                const [programaRes, transacoesRes] = await Promise.all([
                    getProgramaPortadorDetail(programaId),
                    getTransacoesDoPrograma(programaId)
                ]);
                setProgramaDetalhe(programaRes.data);
                setTransacoes(transacoesRes.data);
            }
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao atualizar os dados do extrato.'));
        } finally {
            setLoadingTransacoes(false);
        }
    }, [messageApi]);
    
    // Listener para atualizar o extrato quando ocorrer ação na Sidebar
    useEffect(() => {
        const handleTransactionUpdate = () => {
            if (selectedProgramaId) {
                fetchOnlyExtratoData(selectedProgramaId, selectedPortadorId);
            }
        };
        window.addEventListener('transaction-updated', handleTransactionUpdate);
        return () => {
            window.removeEventListener('transaction-updated', handleTransactionUpdate);
        };
    }, [selectedProgramaId, selectedPortadorId, fetchOnlyExtratoData]);

    useEffect(() => {
        const initialize = async () => {
            setLoadingPortadores(true);
            try {
                const res = await getPortadores();
                const portadoresData = res.data;
                setPortadores(portadoresData);

                const portadorDeepLink = Number.isFinite(deepLinkPortadorId) && deepLinkPortadorId > 0
                    ? portadoresData.find(p => p.id === deepLinkPortadorId)
                    : null;
                const portadorParaSelecionar = portadorDeepLink
                    || portadoresData.find(p => p.is_principal)
                    || (portadoresData.length > 0 ? portadoresData[0] : null);
                
                if (portadorParaSelecionar) {
                    setSelectedPortadorId(portadorParaSelecionar.id);
                }
            } catch (error) {
                messageApi.error(getErrorMessage(error, 'Erro ao buscar a lista de portadores.'));
            } finally {
                setLoadingPortadores(false);
            }
        };

        if (isInitialLoad.current) {
            initialize();
            isInitialLoad.current = false;
        }
    }, [messageApi]);

    useEffect(() => {
        const fetchProgramas = async () => {
            if (!selectedPortadorId) return;

            setLoadingProgramas(true);
            setProgramasDoPortador([]);
            setSelectedProgramaId(null);
            try {
                const res = await getProgramasDoPortador(selectedPortadorId);
                const programasData = res.data;
                setProgramasDoPortador(programasData);

                if (programasData.length > 0) {
                    const pendingId = pendingDeepLinkProgramaId.current;
                    if (pendingId && programasData.some(p => p.id === pendingId)) {
                        setSelectedProgramaId(pendingId);
                        pendingDeepLinkProgramaId.current = null;
                    } else {
                        pendingDeepLinkProgramaId.current = null;
                        setSelectedProgramaId('todos');
                    }
                } else {
                    pendingDeepLinkProgramaId.current = null;
                    setProgramaDetalhe(null);
                    setTransacoes([]);
                }
            } catch (error) {
                messageApi.error(getErrorMessage(error, 'Erro ao buscar os programas do portador.'));
            } finally {
                setLoadingProgramas(false);
            }
        };

        fetchProgramas();
    }, [selectedPortadorId, messageApi]);

    useEffect(() => {
        if (selectedProgramaId) {
            fetchOnlyExtratoData(selectedProgramaId, selectedPortadorId);
        }
    }, [selectedProgramaId, selectedPortadorId, fetchOnlyExtratoData]);

    useEffect(() => {
        if (!isInitialLoad.current && selectedProgramaId) {
            fetchOnlyExtratoData(selectedProgramaId, selectedPortadorId);
        }
    }, [refreshKey, selectedProgramaId, selectedPortadorId, fetchOnlyExtratoData]);
    
    const handleCancelarEmissao = (emissao) => {
        modal.confirm({
            title: 'Cancelar Emissão',
            content: `Você tem certeza que deseja cancelar a emissão para ${emissao.passageiro_nome}? As ${emissao.quantidade_milhas_utilizada.toLocaleString('pt-BR')} milhas serão estornadas para o saldo do programa.`,
            okText: 'Sim, cancelar',
            okType: 'danger',
            cancelText: 'Não',
            onOk: async () => {
                try {
                    await cancelarEmissao(emissao.id);
                    messageApi.success('Emissão cancelada com sucesso!');
                    triggerRefresh();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, 'Erro ao cancelar a emissão.'));
                }
            }
        });
    };

    const handleConfirmarBonus = (record) => {
        const projecao = record.projecao_info;
        modal.confirm({
            title: 'Efetivar Projeção de Bônus',
            content: `Você confirma o recebimento de ${parseFloat(record.quantidade_milhas).toLocaleString('pt-BR')} pontos do ${projecao.descricao} referente a ${dayjs(record.data_transacao).format('DD/MM/YYYY')}?`,
            okText: 'Sim, efetivar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await confirmarBonusClube({
                        id_clube: projecao.id_clube,
                        data_projetada: projecao.data_projetada
                    });
                    messageApi.success('Projeção efetivada com sucesso!');
                    triggerRefresh();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, 'Erro ao efetivar a projeção.'));
                }
            }
        });
    };

    // Cada linha pendente é confirmada sozinha, com a data em que os pontos realmente caíram.
    // Em transferências, principal e bônus podem cair em dias diferentes — confirmar um não afeta o outro.
    const handleConfirmarPendente = (record) => {
        const hoje = dayjs().startOf('day');
        const prevista = dayjs(record.data_transacao);
        let dataEfetiva = prevista.isAfter(hoje) ? hoje : prevista;
        const ehPernaTransferencia = isPernaTransferencia(record);

        modal.confirm({
            title: `Confirmar ${subTipoLabel(record.sub_tipo)}`,
            content: (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text>
                        Confirma o recebimento de {parseFloat(record.quantidade_milhas).toLocaleString('pt-BR')} pontos?
                        {ehPernaTransferencia && ' As outras entradas desta transferência não serão alteradas.'}
                    </Text>
                    <Text type="secondary">Data em que os pontos caíram:</Text>
                    <DatePicker
                        defaultValue={dataEfetiva}
                        format="DD/MM/YYYY"
                        allowClear={false}
                        style={{ width: '100%' }}
                        disabledDate={(d) => d.isAfter(hoje)}
                        onChange={(d) => { if (d) dataEfetiva = d; }}
                    />
                </Space>
            ),
            okText: 'Sim, confirmar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await confirmarTransacaoMilhas(record.id, dataEfetiva.format('YYYY-MM-DD'));
                    messageApi.success('Entrada confirmada com sucesso!');
                    triggerRefresh();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, 'Falha ao confirmar a transação.'));
                    throw error;
                }
            },
        });
    };

    const isPernaTransferencia = (record) => Boolean(
        record?.transferencia_id || record?.transferencia_terceiros_id
    );

    const podeReverterParaPendente = (record) => (
        record?.status === 'CONCLUIDA'
        && record?.sub_tipo !== 'ESTORNO_CANCELAMENTO'
        && !isPernaTransferencia(record)
    );

    const handleReverterParaPendente = (record) => {
        modal.confirm({
            title: 'Reverter para pendente?',
            content: 'A transação voltará a ser prevista (pendente) e deixará de contar no saldo até ser confirmada novamente.',
            okText: 'Sim, reverter',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await reverterTransacaoMilhasParaPendente(record.id);
                    messageApi.success('Transação revertida para pendente.');
                    triggerRefresh();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, 'Falha ao reverter a transação.'));
                }
            },
        });
    };

    const handleDelete = (transacao) => {
        const isTransfer = isPernaTransferencia(transacao);
        modal.confirm({
            title: isTransfer
                ? 'Excluir esta transferência?'
                : 'Você tem certeza que deseja excluir esta transação?',
            content: isTransfer
                ? 'Isso remove todos os débitos e créditos desta transferência. O saldo será recalculado.'
                : 'Essa ação não poderá ser desfeita e o seu saldo será recalculado.',
            okText: 'Sim, excluir',
            okType: 'danger',
            cancelText: 'Não',
            onOk: async () => {
                try {
                    await deleteTransacaoMilhas(transacao.id);
                    messageApi.success(isTransfer ? 'Transferência excluída com sucesso!' : 'Transação excluída com sucesso!');
                    triggerRefresh();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, 'Falha ao excluir a transação.'));
                }
            },
        });
    };

    const handleEdit = async (transacao) => {
        if (isPernaTransferencia(transacao)) {
            messageApi.info(
                'Transferências não podem ser editadas por esta linha. Exclua a transferência para desfazer todos os débitos/créditos.'
            );
            return;
        }
        if (transacao.tipo_transacao === 'ENTRADA') {
            setEditingTransaction(transacao);
            setIsEntradaModalVisible(true);
            return;
        }
        if (transacao.sub_tipo === 'SAIDA_AVULSA') {
            setEditingVenda(null);
            setEditingSaida(transacao);
            setIsSaidaModalVisible(true);
            return;
        }
        if (transacao.sub_tipo === 'SAIDA_VENDA_MILHAS') {
            const vendaId = transacao.venda_info?.id;
            if (!vendaId) {
                messageApi.error('Não foi possível identificar a operação de venda vinculada.');
                return;
            }
            try {
                const venda = await getOperacaoVenda(vendaId);
                setEditingSaida(null);
                setEditingVenda(venda);
                setIsSaidaModalVisible(true);
            } catch (error) {
                messageApi.error(getErrorMessage(error, 'Falha ao carregar a operação de venda.'));
            }
            return;
        }
        messageApi.info('A edição deste tipo de transação de saída será implementada futuramente.');
    };

    const getTipoTransacaoValue = (record) => {
        if (record.status === 'PROJETADA') return 2;
        if (record.tipo_transacao === 'ENTRADA') return 1;
        return 0;
    };

    const getOperacaoDisplay = (record) => operacaoLabel(record);

    const renderProgramaTag = (record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {record.programa_slug && (
                <LogoManager slug={record.programa_slug} type="programa" style={{ width: 24, height: 24 }} />
            )}
            <Tag>{record.programa_nome || '—'}</Tag>
        </div>
    );

    const transacoesColumns = [
        { title: 'Data', dataIndex: 'data_transacao', key: 'data_transacao', sorter: (a, b) => dayjs(a.data_transacao).unix() - dayjs(b.data_transacao).unix(), sortOrder: tableSorters?.field === 'data_transacao' && tableSorters.order, render: (text) => dayjs(text).format('DD/MM/YYYY') },
        ...(isTodosProgramas ? [{
            title: 'Programa',
            dataIndex: 'programa_nome',
            key: 'programa_nome',
            sorter: (a, b) => (a.programa_nome || '').localeCompare(b.programa_nome || ''),
            sortOrder: tableSorters?.field === 'programa_nome' && tableSorters.order,
            render: (_text, record) => renderProgramaTag(record),
        }] : []),
        { 
            title: 'Tipo', 
            dataIndex: 'tipo_transacao', 
            key: 'tipo_transacao', 
            sorter: (a, b) => getTipoTransacaoValue(a) - getTipoTransacaoValue(b),
            sortOrder: tableSorters?.field === 'tipo_transacao' && tableSorters.order,
            render: (text, record) => {
                if (record.status === 'PROJETADA') return <Tag color="geekblue" icon={<ArrowUpOutlined />}>Projeção</Tag>;
                return record.tipo_transacao === 'ENTRADA' ? <Tag color="green" icon={<ArrowUpOutlined />}>Entrada</Tag> : <Tag color="red" icon={<ArrowDownOutlined />}>Saída</Tag>;
            } 
        },
        { 
            title: 'Operação', 
            dataIndex: 'sub_tipo', 
            key: 'sub_tipo', 
            sorter: (a, b) => getOperacaoDisplay(a).localeCompare(getOperacaoDisplay(b)),
            sortOrder: tableSorters?.field === 'sub_tipo' && tableSorters.order,
            render: (text, record) => {
                const operacaoPrincipal = getOperacaoDisplay(record);
                const obsVisivel = stripMarcadorFaturaId(record.observacao);
                const showObs = Boolean(obsVisivel) && !String(record.observacao || '').startsWith('PROJECAO_CLUBE_ID_');
                
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>{operacaoPrincipal}</span>
                            {showObs && (
                                <Tooltip title={obsVisivel}>
                                    <InfoCircleOutlined style={{ marginLeft: 6, color: 'var(--color-text-secondary, #5A6A6A)', fontSize: '14px' }} />
                                </Tooltip>
                            )}
                            {record.venda_info && (
                                <Tooltip title={`Saldo restante da venda: ${record.venda_info.quantidade_milhas_saldo.toLocaleString('pt-BR')} / ${record.venda_info.quantidade_milhas_vendidas.toLocaleString('pt-BR')} pts`}>
                                    <InfoCircleOutlined style={{ marginLeft: 6, color: '#50B0A0', fontSize: '14px' }} />
                                </Tooltip>
                            )}
                        </div>
                        {record.sub_tipo === 'COMPRA_BONIFICADA' && record.detalhes_compra_bonificada && (
                            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5A6A6A)', marginTop: '2px' }}>
                                <ShopOutlined style={{ marginRight: 4 }} /> 
                                {record.detalhes_compra_bonificada.loja} • {record.detalhes_compra_bonificada.produto}
                            </div>
                        )}
                    </div>
                );
            }
        },
        { title: 'Quantidade', dataIndex: 'quantidade_milhas', key: 'quantidade_milhas', align: 'right', render: (valor, record) => ( <Text type={record.tipo_transacao === 'ENTRADA' || record.status === 'PROJETADA' ? 'success' : 'danger'}> {record.tipo_transacao === 'ENTRADA' || record.status === 'PROJETADA' ? '+' : '-'} {parseFloat(valor).toLocaleString('pt-BR')} </Text> ) },
        { title: 'Custo', dataIndex: 'custo_total_transacao', key: 'custo_total_transacao', align: 'right', render: (valor) => parseFloat(valor) ? parseFloat(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <Tag color={{'CONCLUIDA': 'blue', 'PENDENTE': 'orange', 'PROJETADA': 'purple'}[status]}>{status.replace('_', ' ')}</Tag> },
        { 
            title: 'Ações', 
            key: 'acoes', 
            align: 'right',
            render: (text, record) => ( 
                <Space size="small">
                    {record.status === 'PROJETADA' && 
                        <Tooltip title="Efetivar Projeção">
                            <Button type="text" icon={<CheckCircleFilled />} style={{ color: 'var(--ant-color-success)' }} onClick={() => handleConfirmarBonus(record)} />
                        </Tooltip>
                    }
                    {record.status === 'PENDENTE' && 
                        <Tooltip title="Confirmar">
                            <Button type="text" icon={<CheckCircleFilled />} style={{ color: 'var(--ant-color-success)' }} onClick={() => handleConfirmarPendente(record)} />
                        </Tooltip>
                    }
                    {podeReverterParaPendente(record) &&
                        <Tooltip title="Reverter para pendente">
                            <Button type="text" icon={<RollbackOutlined />} onClick={() => handleReverterParaPendente(record)} />
                        </Tooltip>
                    }
                    {record.status !== 'PROJETADA' && record.sub_tipo !== 'ESTORNO_CANCELAMENTO' && (
                        <>
                            {!isPernaTransferencia(record) && (
                                <Tooltip title="Editar">
                                    <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                                </Tooltip>
                            )}
                            <Tooltip title={isPernaTransferencia(record) ? 'Excluir transferência inteira' : 'Excluir'}>
                                <Button type="text" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record)} />
                            </Tooltip>
                        </>
                    )}
                </Space> 
            )
        }
    ];

    // Helper para ações no Card Mobile
    const getMobileCardActions = (record) => {
        const actions = [];
        
        if (record.status === 'PROJETADA') {
            actions.push(
                <span key="confirm" onClick={() => handleConfirmarBonus(record)} style={{ color: '#2F9E6F', width: '100%', display: 'inline-block' }}>
                    <CheckCircleFilled /> Efetivar
                </span>
            );
        } else if (record.status === 'PENDENTE') {
            actions.push(
                <span key="confirm" onClick={() => handleConfirmarPendente(record)} style={{ color: '#2F9E6F', width: '100%', display: 'inline-block' }}>
                    <CheckCircleFilled /> Confirmar
                </span>
            );
        }

        if (podeReverterParaPendente(record)) {
            actions.push(
                <span key="revert" onClick={() => handleReverterParaPendente(record)} style={{ width: '100%', display: 'inline-block' }}>
                    <RollbackOutlined /> Reverter
                </span>
            );
        }

        if (record.status !== 'PROJETADA' && record.sub_tipo !== 'ESTORNO_CANCELAMENTO') {
            if (!isPernaTransferencia(record)) {
                actions.push(
                    <span key="edit" onClick={() => handleEdit(record)} style={{ width: '100%', display: 'inline-block' }}>
                        <EditOutlined /> Editar
                    </span>
                );
            }
            actions.push(
                <span key="delete" onClick={() => handleDelete(record)} style={{ color: '#ff4d4f', width: '100%', display: 'inline-block' }}>
                    <DeleteOutlined /> {isPernaTransferencia(record) ? 'Excluir transferência' : 'Excluir'}
                </span>
            );
        }
        
        return actions.length > 0 ? actions : null;
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '0' }}>
            {modalContextHolder}
            {messageContextHolder}
            
            <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: isMobile ? '16px' : '24px' }}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={8}><Title level={2} style={{ margin: 0 }}>Movimentações</Title></Col>
                    <Col xs={24} md={16}>
                        {/* BOTÕES DE AÇÃO */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, auto)', gap: '8px', justifyContent: isMobile ? 'stretch' : 'end' }}>
                            <Button icon={<UserSwitchOutlined />} onClick={() => setIsTransferenciaTerceirosModalVisible(true)} block={isMobile}>
                                Transf. Pessoas
                            </Button>
                            <Button icon={<SwapOutlined />} onClick={() => setIsTransferenciaModalVisible(true)} block={isMobile}>
                                Transf. Programas
                            </Button>
                            <Button icon={<ArrowDownOutlined />} onClick={() => setIsSaidaModalVisible(true)} block={isMobile}>
                                Nova Saída
                            </Button>
                            <Button type="primary" icon={<ArrowUpOutlined />} onClick={() => { setEditingTransaction(null); setIsEntradaModalVisible(true); }} block={isMobile}>
                                Nova Entrada
                            </Button>
                        </div>
                    </Col>
                </Row>
                
                {/* FILTROS RESPONSIVOS - CORREÇÃO:
                    Usamos uma div com display flex para controle manual.
                    Mobile: flex-direction: column (empilhados e width: 100%)
                    Desktop: flex-direction: row (lado a lado e width: fixo)
                */}
                <div style={{ 
                    marginTop: 24, 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 12,
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        flexWrap: 'wrap',
                        gap: 16,
                        alignItems: isMobile ? 'stretch' : 'flex-end',
                    }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 200 }}>
                            <div style={{ fontWeight: 500 }}>Portador</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Selecione um portador" 
                                loading={loadingPortadores} 
                                value={selectedPortadorId} 
                                onChange={setSelectedPortadorId}
                                size={isMobile ? 'large' : 'middle'}
                            >
                                {portadores.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}
                            </Select>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 200 }}>
                            <div style={{ fontWeight: 500 }}>Programa</div>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Selecione um programa"
                                disabled={!selectedPortadorId || loadingProgramas}
                                loading={loadingProgramas}
                                value={selectedProgramaId}
                                onChange={setSelectedProgramaId}
                                size={isMobile ? 'large' : 'middle'}
                            >
                                {programasDoPortador.length > 0 && (
                                    <Option value="todos">Todos os programas</Option>
                                )}
                                {programasDoPortador.map(p => (
                                    <Option key={p.id} value={p.id}>{p.programa_nome}</Option>
                                ))}
                            </Select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 160 }}>
                            <div style={{ fontWeight: 500 }}>Status</div>
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Todos"
                                value={filtroStatus}
                                onChange={setFiltroStatus}
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                                size={isMobile ? 'large' : 'middle'}
                                options={Object.entries(STATUS_MILHAS_LABELS).map(([value, label]) => ({ value, label }))}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 140 }}>
                            <div style={{ fontWeight: 500 }}>Tipo</div>
                            <Select
                                allowClear
                                placeholder="Todos"
                                value={filtroTipo}
                                onChange={setFiltroTipo}
                                style={{ width: '100%' }}
                                size={isMobile ? 'large' : 'middle'}
                                options={[
                                    { value: 'ENTRADA', label: 'Entrada' },
                                    { value: 'SAIDA', label: 'Saída' },
                                ]}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 200 }}>
                            <div style={{ fontWeight: 500 }}>Operação</div>
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Todas"
                                value={filtroSubTipos}
                                onChange={setFiltroSubTipos}
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                                size={isMobile ? 'large' : 'middle'}
                                options={subTipoOptions}
                                disabled={subTipoOptions.length === 0}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 160 }}>
                            <div style={{ fontWeight: 500 }}>Período</div>
                            <DatePicker
                                picker="month"
                                allowClear
                                placeholder="Todos os meses"
                                value={filtroMes}
                                onChange={setFiltroMes}
                                format="MMM/YYYY"
                                style={{ width: '100%' }}
                                size={isMobile ? 'large' : 'middle'}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 220, flex: isMobile ? undefined : '1 1 180px' }}>
                            <div style={{ fontWeight: 500 }}>Busca</div>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder="Obs., operação, loja…"
                                value={filtroBusca}
                                onChange={(e) => setFiltroBusca(e.target.value)}
                                size={isMobile ? 'large' : 'middle'}
                            />
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 16,
                        justifyContent: 'space-between',
                    }}>
                        <Space size="middle" wrap>
                            <Space size={8}>
                                <Switch
                                    checked={filtroAReceber}
                                    onChange={setFiltroAReceber}
                                    checkedChildren={<ClockCircleOutlined />}
                                />
                                <Text>Só a receber</Text>
                            </Space>
                            {temFiltrosAtivos && (
                                <Button type="link" icon={<ClearOutlined />} onClick={limparFiltrosLista} style={{ paddingInline: 0 }}>
                                    Limpar filtros
                                </Button>
                            )}
                        </Space>
                        {(programaDetalhe || isTodosProgramas) && !loadingTransacoes && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {transacoesExibidas.length} de {transacoes.length} lançamento(s)
                            </Text>
                        )}
                    </div>
                </div>
            </Card>

            <TransacaoMilhasModal
                visible={isEntradaModalVisible}
                onClose={() => { setIsEntradaModalVisible(false); setEditingTransaction(null); }}
                onSuccess={(shouldClose, msg) => { 
                    if (msg) messageApi.success(msg);
                    if (shouldClose) { setIsEntradaModalVisible(false); setEditingTransaction(null); }
                    triggerRefresh(); 
                }}
                onRefresh={triggerRefresh}
                editingTransaction={editingTransaction}
                isMobile={isMobile}
            />
            
            <SaidaMilhasModal
                visible={isSaidaModalVisible}
                onClose={() => { setIsSaidaModalVisible(false); setEditingSaida(null); setEditingVenda(null); }}
                onSuccess={(shouldClose, msg) => {
                    if (msg) messageApi.success(msg);
                    if (shouldClose !== false) {
                        setIsSaidaModalVisible(false);
                        setEditingSaida(null);
                        setEditingVenda(null);
                    }
                    triggerRefresh();
                }}
                editingTransaction={editingSaida}
                editingVenda={editingVenda}
                isMobile={isMobile}
            />

            <TransferenciaMilhasModal
                visible={isTransferenciaModalVisible}
                onClose={() => setIsTransferenciaModalVisible(false)}
                onSuccess={() => { setIsTransferenciaModalVisible(false); triggerRefresh(); }}
                isMobile={isMobile}
            />

            <TransferenciaTerceirosModal
                visible={isTransferenciaTerceirosModalVisible}
                onClose={() => setIsTransferenciaTerceirosModalVisible(false)}
                onSuccess={() => { setIsTransferenciaTerceirosModalVisible(false); triggerRefresh(); }}
                isMobile={isMobile}
            />

            {loadingTransacoes ? (
                <div className="brand-skeleton" style={{ padding: 24 }} aria-busy="true" aria-label="Carregando">
                    <div className="brand-skeleton-line brand-skeleton-line--lg" />
                    <div className="brand-skeleton-line brand-skeleton-line--md" />
                    <div className="brand-skeleton-line" />
                    <div className="brand-skeleton-line brand-skeleton-line--sm" />
                </div>
            ) : (
                (programaDetalhe || isTodosProgramas) ? (
                    <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                         <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col span={24}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                                    {!isTodosProgramas && programaDetalhe && (
                                        <LogoManager slug={programaDetalhe.programa_slug} type="programa" style={{ width: '60px', height: '60px' }}/>
                                    )}
                                    <div>
                                        <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
                                            {isTodosProgramas
                                                ? 'Todos os programas'
                                                : programaDetalhe?.programa_nome}
                                        </Title>
                                        <Text type="secondary">
                                            Extrato de {isTodosProgramas
                                                ? (portadorSelecionado?.nome_completo || '—')
                                                : programaDetalhe?.portador_nome}
                                        </Text>
                                    </div>
                                </div>
                                
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Card bordered={false} style={{ background: 'rgba(47, 158, 111, 0.10)', borderColor: 'rgba(47, 158, 111, 0.35)', height: '100%' }} bodyStyle={{ padding: '16px' }}>
                                            <Statistic 
                                                title="Saldo Disponível" 
                                                value={(isTodosProgramas
                                                    ? totaisAgregados.saldo
                                                    : parseFloat(programaDetalhe.saldo_atual)
                                                ).toLocaleString('pt-BR')} 
                                                suffix="pts"
                                                valueStyle={{ color: '#2F9E6F', fontSize: isMobile ? '1.5rem' : '1.75rem' }} 
                                            />
                                        </Card>
                                    </Col>
                                    
                                    <Col xs={24} sm={12} md={6}>
                                        <Card bordered={false} style={{ background: 'rgba(230, 162, 60, 0.12)', borderColor: 'rgba(230, 162, 60, 0.40)', height: '100%' }} bodyStyle={{ padding: '16px' }}>
                                            <Statistic 
                                                title="A Receber (Projeção)" 
                                                value={milhasAReceber.toLocaleString('pt-BR')} 
                                                suffix="pts"
                                                valueStyle={{ color: '#E6A23C', fontSize: isMobile ? '1.5rem' : '1.75rem' }}
                                                prefix={<ClockCircleOutlined />}
                                            />
                                        </Card>
                                    </Col>

                                    {isTodosProgramas ? (
                                        <Col xs={24} sm={12} md={6}>
                                            <Card bordered={false} style={{ background: 'rgba(80, 176, 160, 0.12)', borderColor: 'rgba(80, 176, 160, 0.40)', height: '100%' }} bodyStyle={{ padding: '16px' }}>
                                                <Statistic 
                                                    title="Programas" 
                                                    value={totaisAgregados.qtdProgramas}
                                                    valueStyle={{ color: '#50B0A0', fontSize: isMobile ? '1.5rem' : '1.75rem' }}
                                                />
                                            </Card>
                                        </Col>
                                    ) : (
                                        <Col xs={24} sm={12} md={6}>
                                            <Card bordered={false} style={{ background: 'rgba(80, 176, 160, 0.12)', borderColor: 'rgba(80, 176, 160, 0.40)', height: '100%' }} bodyStyle={{ padding: '16px' }}>
                                                <Statistic 
                                                    title="Custo Médio (CPM)" 
                                                    value={parseFloat(programaDetalhe.custo_medio_milheiro).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                                                    valueStyle={{ color: '#50B0A0', fontSize: isMobile ? '1.5rem' : '1.75rem' }}
                                                />
                                            </Card>
                                        </Col>
                                    )}
                                    
                                    <Col xs={24} sm={12} md={6}>
                                        <Card bordered={false} style={{ background: 'rgba(31, 79, 79, 0.08)', borderColor: 'rgba(31, 79, 79, 0.25)', height: '100%' }} bodyStyle={{ padding: '16px' }}>
                                            <Statistic 
                                                title="Investimento Total" 
                                                value={(isTodosProgramas
                                                    ? totaisAgregados.investimento
                                                    : parseFloat(programaDetalhe.valor_total_investido)
                                                ).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} 
                                                valueStyle={{ color: '#2F6B6B', fontSize: isMobile ? '1.5rem' : '1.75rem' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        {/* TABELA OU LISTA MOBILE */}
                        {(() => {
                            const semDadosBase = !transacoes.length;
                            const emptyExtrato = (
                                <EmptyStateGuide
                                    title={
                                        portadores.length === 0
                                            ? 'Nenhum portador cadastrado.'
                                            : !selectedPortadorId
                                              ? 'Selecione um portador.'
                                              : programasDoPortador.length === 0
                                                ? 'Nenhum programa ativo neste portador.'
                                                : temFiltrosAtivos && !semDadosBase
                                                  ? 'Nenhum lançamento com estes filtros.'
                                                  : 'Nenhuma movimentação neste extrato.'
                                    }
                                    description={
                                        portadores.length === 0
                                            ? 'Cadastre um portador e ative um programa para registrar milhas.'
                                            : !selectedPortadorId
                                              ? 'Escolha o portador acima para ver o extrato.'
                                              : programasDoPortador.length === 0
                                                ? 'Use Ativar programa em Gerenciar Portadores (na linha da pessoa) para começar a lançar movimentações.'
                                                : temFiltrosAtivos && !semDadosBase
                                                  ? 'Ajuste ou limpe os filtros para ver mais movimentações.'
                                                  : 'Registre uma entrada de milhas para começar o histórico.'
                                    }
                                    actionLabel={
                                        portadores.length === 0
                                            ? 'Ir para Portadores'
                                            : programasDoPortador.length === 0 && selectedPortadorId
                                              ? 'Ir para Portadores'
                                              : temFiltrosAtivos && !semDadosBase
                                                ? 'Limpar filtros'
                                                : selectedPortadorId && programasDoPortador.length > 0
                                                  ? 'Nova entrada'
                                                  : undefined
                                    }
                                    onAction={
                                        portadores.length === 0
                                            ? () => navigate('/milhas/portadores')
                                            : programasDoPortador.length === 0 && selectedPortadorId
                                              ? () => navigate('/milhas/portadores')
                                              : temFiltrosAtivos && !semDadosBase
                                                ? limparFiltrosLista
                                                : selectedPortadorId && programasDoPortador.length > 0
                                                  ? () => { setEditingTransaction(null); setIsEntradaModalVisible(true); }
                                                  : undefined
                                    }
                                    icon={portadores.length === 0 || programasDoPortador.length === 0 ? null : <ArrowUpOutlined />}
                                />
                            );
                            return isMobile ? (
                            <List
                                dataSource={transacoesExibidas}
                                locale={{ emptyText: emptyExtrato }}
                                renderItem={item => (
                                    <Card 
                                        style={{ marginBottom: 12 }}
                                        bodyStyle={{ padding: '12px' }}
                                        actions={getMobileCardActions(item)}
                                    >
                                        {/* Header do Card */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <CalendarOutlined style={{ color: 'var(--color-text-secondary, #5A6A6A)' }} />
                                                <Text strong>{dayjs(item.data_transacao).format('DD/MM/YYYY')}</Text>
                                            </div>
                                            <Tag color={{'CONCLUIDA': 'blue', 'PENDENTE': 'orange', 'PROJETADA': 'purple'}[item.status]}>
                                                {item.status === 'PROJETADA' ? 'PROJEÇÃO' : item.status}
                                            </Tag>
                                        </div>

                                        {isTodosProgramas && (
                                            <div style={{ marginBottom: 8 }}>
                                                {renderProgramaTag(item)}
                                            </div>
                                        )}

                                        {/* Corpo */}
                                        <Row justify="space-between" align="middle">
                                            <Col span={14}>
                                                <Text style={{ fontSize: '1rem', display: 'block' }}>{getOperacaoDisplay(item)}</Text>
                                                {item.sub_tipo === 'COMPRA_BONIFICADA' && item.detalhes_compra_bonificada && (
                                                    <Text type="secondary" style={{ fontSize: '0.8rem', display: 'block' }}>
                                                        <ShopOutlined /> {item.detalhes_compra_bonificada.loja} • {item.detalhes_compra_bonificada.produto}
                                                    </Text>
                                                )}
                                            </Col>
                                            <Col span={10} style={{ textAlign: 'right' }}>
                                                <Text strong style={{ fontSize: '1.2rem', color: item.tipo_transacao === 'ENTRADA' || item.status === 'PROJETADA' ? '#2F9E6F' : '#D64545' }}>
                                                    {item.tipo_transacao === 'ENTRADA' || item.status === 'PROJETADA' ? '+' : '-'}
                                                    {parseFloat(item.quantidade_milhas).toLocaleString('pt-BR')}
                                                </Text>
                                                {parseFloat(item.custo_total_transacao) > 0 && (
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary, #5A6A6A)' }}>
                                                        {parseFloat(item.custo_total_transacao).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>

                                        {/* Emissões Vinculadas (Expansão) */}
                                        {item.emissoes && item.emissoes.length > 0 && (
                                            <div style={{ marginTop: 12, padding: '8px', background: 'var(--color-surface-muted, #F8F4EC)', borderRadius: 4, border: '1px solid #f0f0f0' }}>
                                                <Text type="secondary" style={{ fontSize: '0.8rem', fontWeight: 600 }}>EMISSÕES VINCULADAS:</Text>
                                                {item.emissoes.map(e => (
                                                    <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, fontSize: '0.85rem' }}>
                                                        <span>{e.passageiro_nome} ({e.quantidade_milhas_utilizada.toLocaleString('pt-BR')} pts)</span>
                                                        {e.status === 'EMITIDA' && (
                                                            <Button type="link" danger size="small" icon={<CloseCircleOutlined />} onClick={() => handleCancelarEmissao(e)} />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                )}
                            />
                        ) : (
                            <Table 
                                columns={transacoesColumns} 
                                dataSource={transacoesExibidas} 
                                rowKey="id" 
                                pagination={{ pageSize: 25 }} 
                                onChange={handleTableChange}
                                scroll={{ x: 800 }} 
                                expandable={{
                                    expandedRowRender: record => record.emissoes && record.emissoes.length > 0 ? (
                                        <div style={{ padding: '8px 16px', backgroundColor: 'var(--color-surface-muted, #F8F4EC)' }}>
                                            <Text strong>Emissões Vinculadas:</Text>
                                            <ul style={{ paddingLeft: '20px', margin: '8px 0 0 0' }}>
                                                {record.emissoes.map(e => (
                                                    <li key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span>
                                                            {e.passageiro_nome} - {e.quantidade_milhas_utilizada.toLocaleString('pt-BR')} pts {e.codigo_localizador && `(Localizador: ${e.codigo_localizador})`}
                                                            <Tag color={e.status === 'EMITIDA' ? 'success' : 'error'} style={{ marginLeft: 8 }}>{e.status}</Tag>
                                                        </span>
                                                        {e.status === 'EMITIDA' && (
                                                            <Button type="link" danger size="small" icon={<CloseCircleOutlined />} onClick={() => handleCancelarEmissao(e)}>Cancelar</Button>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : null,
                                    rowExpandable: record => record.emissoes && record.emissoes.length > 0,
                                }}
                                locale={{ emptyText: emptyExtrato }}
                            />
                        );
                        })()}
                    </Card>
                ) : (
                    <Card style={{ textAlign: 'center' }}>
                        <EmptyStateGuide
                            title="Selecione um portador e um programa."
                            description="Escolha o portador acima (e um programa) para ver o extrato de milhas."
                            actionLabel={portadores.length === 0 ? 'Ir para Portadores' : undefined}
                            onAction={portadores.length === 0 ? () => navigate('/milhas/portadores') : undefined}
                            icon={null}
                        />
                    </Card>
                )
            )}
        </div>
    );
};

export default ExtratoMilhasPage;